import { useParams, useNavigate, Link } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../context";
import Meta from "../components/Meta";
import Data from "./Data";
import MainData from "../../repository";
import GoBack from "../experiences/components/GoBack";
import SumupWidget from "../components/SumupWidget";
import {X} from "react-feather";

const Service = () => {
    const context = useContext(AppContext);
    const { id } = useParams();
    const navigate = useNavigate();
    if (!id) window.location.href = "/";

    const [service, setService] = useState(null);
    const [staff, setStaff] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [availability, setAvailability] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [customerNotes, setCustomerNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('processor');
    const [loading, setLoading] = useState(true);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [booking, setBooking] = useState(false);
    const [error, setError] = useState(null);
    const [sumupCheckoutId, setSumupCheckoutId] = useState(null);
    const [sumupBookingId, setSumupBookingId] = useState(null);
    const [sumupError, setSumupError] = useState(null);
    const [waitlistKeys, setWaitlistKeys] = useState(new Set());
    const [waitlistLoading, setWaitlistLoading] = useState(null);
    const [waitlistMessage, setWaitlistMessage] = useState(null);
    const [discountCode, setDiscountCode] = useState('');
    const [discountCodeInput, setDiscountCodeInput] = useState('');
    const [discountCodeData, setDiscountCodeData] = useState(null);
    const [discountCodeError, setDiscountCodeError] = useState(null);
    const [discountCodeLoading, setDiscountCodeLoading] = useState(false);
    const sumupPollRef = useRef(null);
    const autoBookRef = useRef(false);

    const isAuthenticated = !!localStorage.getItem("token");
    const isSumup = context.domain?.paymentProcessor === 'SumUp';
    const walletEnabled = !!context.domain?.walletEnabled;
    const walletBalance = Number(context.user?.walletBalance || 0);
    const cartTotal = selectedSlots.length * Number(service?.price || 0);
    const discountedTotal = discountCodeData ? Math.round(cartTotal - (cartTotal * (discountCodeData.percentageOff / 100))) : cartTotal;
    const canUseWallet = isAuthenticated && walletEnabled && cartTotal > 0 && walletBalance >= cartTotal;

    const today = new Date();
    const dates = Array.from({ length: 14 }, (_, i) => {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        return {
            date: date.toISOString().split('T')[0],
            day: date.toLocaleDateString('en-GB', { weekday: 'short' }),
            dayNum: date.getDate(),
            month: date.toLocaleDateString('en-GB', { month: 'short' })
        };
    });

    useEffect(() => {
        const fetchServiceAndStaff = async () => {
            try {
                const [serviceRes, staffRes] = await Promise.all([
                    Data.fetchService(id),
                    Data.fetchServiceStaff(id)
                ]);
                setService(serviceRes.data);
                setStaff(staffRes.data);
                if (staffRes.data.length === 1) setSelectedStaff(staffRes.data[0]);
                if (isAuthenticated) {
                    const waitlistRes = await MainData.fetchWaitlist();
                    setWaitlistKeys(new Set(waitlistRes.data.filter(entry => entry.type === "variable").map(entry => entry.key)));

                    // Restore a pending booking that was saved before the user was redirected to login
                    try {
                        const pendingRaw = localStorage.getItem('pendingVariableBooking');
                        if (pendingRaw) {
                            const pending = JSON.parse(pendingRaw);
                            if (pending.serviceId === id && pending.slots?.length) {
                                const matchingStaff = staffRes.data.find(s => s._id === pending.staffId);
                                if (matchingStaff) {
                                    localStorage.removeItem('pendingVariableBooking');
                                    setSelectedStaff(matchingStaff);
                                    setSelectedSlots(pending.slots);
                                    if (pending.customerNotes) setCustomerNotes(pending.customerNotes);
                                    if (pending.paymentMethod) setPaymentMethod(pending.paymentMethod);
                                    if (pending.discountCode) {
                                        setDiscountCode(pending.discountCode);
                                        setDiscountCodeInput(pending.discountCode);
                                    }
                                    autoBookRef.current = true;
                                }
                            }
                        }
                    } catch {}
                }
            } catch {
                window.location.href = "/";
            } finally {
                setLoading(false);
            }
        };
        fetchServiceAndStaff();
    }, [id]);

    useEffect(() => {
        if (!selectedStaff || !selectedDate) return;
        const fetchAvailability = async () => {
            setLoadingSlots(true);
            setSelectedTime(null);
            try {
                const res = await Data.fetchServiceAvailabilityWithUnavailable(id, selectedDate, selectedDate, selectedStaff._id);
                const staffData = res.data.find(s => s.staff._id === selectedStaff._id);
                if (staffData) {
                    const dateData = staffData.dates.find(d => d.date === selectedDate);
                    setAvailability(dateData?.slots || []);
                } else {
                    setAvailability([]);
                }
            } catch {
                setAvailability([]);
            } finally {
                setLoadingSlots(false);
            }
        };
        fetchAvailability();
    }, [id, selectedStaff, selectedDate]);

    useEffect(() => {
        if (!sumupCheckoutId || !sumupBookingId) return;
        if (sumupPollRef.current) clearInterval(sumupPollRef.current);
        let cancelled = false;
        const poll = async () => {
            try {
                const result = await MainData.verifySumupBulkServiceBookings(sumupBookingId);
                if (!cancelled && result.status === 200) {
                    localStorage.removeItem('pendingServiceBulkCheckoutId');
                    navigate(`/dashboard/my-service-bookings?success=true`);
                }
            } catch {}
        };
        poll();
        sumupPollRef.current = setInterval(poll, 4000);
        return () => {
            cancelled = true;
            if (sumupPollRef.current) { clearInterval(sumupPollRef.current); sumupPollRef.current = null; }
        };
    }, [sumupCheckoutId, sumupBookingId, navigate]);

    const getSlotKey = (slot) => `${slot.staffId}-${slot.date}-${slot.startTime}`;
    const getWaitlistKey = (slot) => `variable:${id}:${slot.staffId}:${slot.date}:${slot.startTime}`;

    const toggleSlot = (slot) => {
        if (slot.available === false || slot.booked) return;
        setError(null);
        const key = getSlotKey(slot);
        setSelectedSlots((current) => {
            if (current.some(item => getSlotKey(item) === key)) {
                return current.filter(item => getSlotKey(item) !== key);
            }
            return [...current, slot];
        });
    };

    const toggleWaitlist = async (slot) => {
        if (!isAuthenticated) {
            navigate(`/login?referer=/services/${id}`);
            return;
        }

        const key = getWaitlistKey(slot);
        setWaitlistLoading(key);
        setError(null);
        setWaitlistMessage(null);

        const payload = {
            type: "variable",
            serviceId: id,
            staffId: slot.staffId,
            date: slot.date,
            startTime: slot.startTime
        };

        try {
            if (waitlistKeys.has(key)) {
                await MainData.leaveWaitlist(payload);
                setWaitlistKeys(current => {
                    const next = new Set(current);
                    next.delete(key);
                    return next;
                });
                setWaitlistMessage("You've left the waitlist.");
            } else {
                await MainData.joinWaitlist(payload);
                setWaitlistKeys(current => new Set([...current, key]));
                setWaitlistMessage("You're on the waitlist. We'll email you if this time becomes available.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Couldn't update the waitlist.");
        } finally {
            setWaitlistLoading(null);
        }
    };

    useEffect(() => {
        if (autoBookRef.current && selectedSlots.length > 0 && selectedStaff && !loading) {
            autoBookRef.current = false;
            handleBook();
        }
    }, [selectedSlots, selectedStaff, loading]);

    const applyDiscountCode = async () => {
        if (!discountCodeInput.trim()) return;
        setDiscountCodeLoading(true);
        setDiscountCodeError(null);
        try {
            const res = await MainData.checkDiscountCode(discountCodeInput.trim());
            setDiscountCodeData(res.data);
            setDiscountCode(discountCodeInput.trim());
        } catch {
            setDiscountCodeData(null);
            setDiscountCode('');
            setDiscountCodeError('Invalid or expired discount code.');
        } finally {
            setDiscountCodeLoading(false);
        }
    };

    const removeDiscountCode = () => {
        setDiscountCode('');
        setDiscountCodeInput('');
        setDiscountCodeData(null);
        setDiscountCodeError(null);
    };

    const handleBook = async () => {
        if (!isAuthenticated) {
            localStorage.setItem('pendingVariableBooking', JSON.stringify({
                serviceId: id,
                staffId: selectedStaff._id,
                slots: selectedSlots,
                customerNotes,
                paymentMethod,
                discountCode: discountCode || undefined
            }));
            navigate(`/login?referer=/services/${id}`);
            return;
        }
        if (!selectedStaff || !selectedSlots.length) return;
        setBooking(true);
        setError(null);
        setSumupError(null);
        try {
            const res = await MainData.createBulkServiceBookings({
                bookings: selectedSlots.map(slot => ({
                    serviceId: id,
                    staffId: slot.staffId,
                    date: slot.date,
                    startTime: slot.startTime,
                    customerNotes
                })),
                paymentMethod,
                discountCode: discountCode || undefined
            });
            if (paymentMethod === 'wallet') {
                await context.fetchUser();
                navigate(`/dashboard/my-service-bookings?success=true`);
                return;
            }
            if (isSumup) {
                const checkoutId = res.data?.checkoutId || res.data?.id || null;
                const bookingId = res.data?.bulkReference || checkoutId;
                if (!checkoutId || !bookingId) throw new Error("Missing SumUp checkout details");
                localStorage.setItem('pendingServiceBulkCheckoutId', checkoutId);
                setSumupCheckoutId(checkoutId);
                setSumupBookingId(bookingId);
            } else if (res.data.redirectUrl) {
                window.location.href = res.data.redirectUrl;
            } else {
                navigate(`/dashboard/my-service-bookings?success=true`);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create booking');
            setBooking(false);
        }
    };

    const formatPrice = (price) => price === 0 ? 'Free' : `£${(price / 100).toFixed(2)}`;
    const getInitials = (f, l) => `${f?.charAt(0) || ''}${l?.charAt(0) || ''}`;

    return (
        <>
            <Meta title={service?.name} />
            <div>
                <GoBack to={"/"} label={"View all services"} />

                {loading ? (
                    <div className={"p-8 container mx-auto"}>
                        <p>Loading...</p>
                    </div>
                ) : (
                    <div className={"flex flex-col xl:flex-row xl:container xl:mx-auto xl:px-8"}>
                        <div className={"bg-gray-100 w-full aspect-video sm:h-80 sm:aspect-auto flex items-center justify-center relative lg:container lg:mx-auto lg:m-8 lg:rounded-lg overflow-hidden"}>
                            {service.image ?
                                <img src={service.image} alt={service.name} className={"w-full h-full object-cover"} />
                                : <div className={"text-6xl text-gray-300"}>{service.name.charAt(0)}</div>
                            }
                        </div>

                        <div className={"p-8 container mx-auto max-w-2xl"}>
                            <h1 className={"text-2xl text-primary"}>{service.name}</h1>
                            <div className={"[&_*]:text-textGrey flex gap-2 mt-1"}>
                                <p>{service.duration} minutes</p>
                                <p>•</p>
                                <p>{formatPrice(service.price)}</p>
                            </div>

                            {service.description && (
                                <p className={"mt-4 text-textGrey"}>{service.description}</p>
                            )}

                            {/* Staff Selection */}
                            {staff.length > 1 && (
                                <div className={"mt-8"}>
                                    <h2 className={"text-lg mb-3"}>Select Staff Member</h2>
                                    <div className={"flex flex-col gap-2"}>
                                        {staff.map(member => (
                                            <div
                                                key={member._id}
                                                onClick={() => { setSelectedStaff(member); setSelectedDate(null); setSelectedTime(null); }}
                                                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${selectedStaff?._id === member._id ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200'}`}
                                            >
                                                <div className={"w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"}>
                                                    {member.image
                                                        ? <img src={member.image} alt={member.firstName} className={"w-full h-full object-cover"} />
                                                        : <span className={"text-sm font-medium text-gray-500"}>{getInitials(member.firstName, member.lastName)}</span>
                                                    }
                                                </div>
                                                <div>
                                                    <p className={"font-medium"}>{member.firstName} {member.lastName}</p>
                                                    {member.title && <p className={"text-sm text-textGrey"}>{member.title}</p>}
                                                </div>
                                                {selectedStaff?._id === member._id && <span className={"ml-auto text-primary"}>✓</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {staff.length === 1 && (
                                <div className={"mt-8"}>
                                    <h2 className={"text-lg mb-3"}>Your Appointment With</h2>
                                    <div className={"flex items-center gap-3 p-3 border border-primary rounded-lg bg-primary bg-opacity-5"}>
                                        <div className={"w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"}>
                                            {staff[0].image
                                                ? <img src={staff[0].image} alt={staff[0].firstName} className={"w-full h-full object-cover"} />
                                                : <span className={"text-sm font-medium text-gray-500"}>{getInitials(staff[0].firstName, staff[0].lastName)}</span>
                                            }
                                        </div>
                                        <div>
                                            <p className={"font-medium"}>{staff[0].firstName} {staff[0].lastName}</p>
                                            {staff[0].title && <p className={"text-sm text-textGrey"}>{staff[0].title}</p>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Date & Time Selection */}
                            {selectedStaff && (
                                <div className={"mt-8"}>
                                    <h2 className={"text-lg mb-3"}>Select Date & Time</h2>
                                    <div className={"flex gap-2 overflow-x-auto pb-2"}>
                                        {dates.map(d => (
                                            <div
                                                key={d.date}
                                                onClick={() => setSelectedDate(d.date)}
                                                className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer min-w-[60px] ${selectedDate === d.date ? 'border-primary bg-primary text-white [&_*]:text-white' : 'border-gray-200'}`}
                                            >
                                                <span className={"text-xs"}>{d.day}</span>
                                                <span className={"text-lg font-semibold"}>{d.dayNum}</span>
                                                <span className={"text-xs"}>{d.month}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {selectedDate && (
                                        <div className={"mt-4"}>
                                            {loadingSlots ? (
                                                <p className={"text-textGrey text-sm"}>Loading available times...</p>
                                            ) : availability.length > 0 ? (
                                                <div className={"flex flex-wrap gap-2"}>
                                                    {availability.map(slot => {
                                                        const cartSlot = { staffId: selectedStaff._id, date: selectedDate, startTime: slot.startTime, available: slot.available, booked: slot.booked };
                                                        const selected = selectedSlots.some(item => getSlotKey(item) === getSlotKey(cartSlot));
                                                        const unavailable = slot.available === false || slot.booked;
                                                        const waitlistKey = getWaitlistKey(cartSlot);
                                                        const waitlisted = waitlistKeys.has(waitlistKey);
                                                        return (
                                                        <div
                                                            key={slot.startTime}
                                                            onClick={() => {
                                                                if (unavailable) return;
                                                                setSelectedTime(slot.startTime);
                                                                toggleSlot(cartSlot);
                                                            }}
                                                            className={`px-4 py-2 border rounded-md text-sm ${unavailable ? 'border-gray-200 bg-gray-100 text-textGrey' : 'cursor-pointer'} ${selected ? 'border-primary bg-primary text-white' : 'border-gray-200'}`}
                                                        >
                                                            <span>{slot.startTime}</span>
                                                            {selected && <span className={"block text-xs mt-1 text-white font-semibold"}>In cart</span>}
                                                            {unavailable && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleWaitlist(cartSlot);
                                                                    }}
                                                                    disabled={waitlistLoading === waitlistKey}
                                                                    className={`block mt-2 border-0 px-3 py-1 rounded-md text-xs ${waitlisted ? 'bg-primary text-white' : 'bg-white text-textGrey'}`}
                                                                >
                                                                    {waitlistLoading === waitlistKey ? 'Updating...' : waitlisted ? 'Leave waitlist' : 'Join waitlist'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )})}
                                                </div>
                                            ) : (
                                                <p className={"text-textGrey text-sm"}>No available times for this date. Please try another date.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Notes — only shown to authenticated users after selecting a time */}
                            {selectedSlots.length > 0 && isAuthenticated && (
                                <div className={"mt-8"}>
                                    <h2 className={"text-lg mb-3"}>Additional Notes (Optional)</h2>
                                    <textarea
                                        className={"w-full border border-gray-200 rounded-lg p-3 text-sm"}
                                        rows={3}
                                        placeholder={"Any special requests or information..."}
                                        value={customerNotes}
                                        onChange={e => setCustomerNotes(e.target.value)}
                                    />
                                </div>
                            )}

                            {selectedSlots.length > 0 && (
                                <div className={"mt-8"}>
                                    <h2 className={"text-lg mb-3"}>Cart</h2>
                                    <div className={"flex flex-col gap-2"}>
                                        {selectedSlots.map(slot => (
                                            <div key={getSlotKey(slot)} className={"border border-gray-200 rounded-lg p-3 text-sm flex items-center justify-between gap-3"}>
                                                <span>{new Date(slot.date).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })} at {slot.startTime}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSlot(slot)}
                                                    aria-label={`Remove ${slot.startTime} from cart`}
                                                    className={"border-0 bg-gray-100 p-2 rounded-md hover:bg-red-100 hover:text-red-700 transition"}
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedSlots.length > 0 && isAuthenticated && service?.price > 0 && (
                                <div className={"mt-8"}>
                                    <h2 className={"text-lg mb-3"}>Discount Code</h2>
                                    {discountCodeData ? (
                                        <div className={"flex items-center justify-between p-3 border border-primary rounded-lg bg-primary bg-opacity-5 text-sm"}>
                                            <span className={"text-primary font-medium"}>{discountCode} — {discountCodeData.percentageOff}% off applied</span>
                                            <button type="button" onClick={removeDiscountCode} className={"border-0 bg-transparent text-textGrey text-xs underline"}>Remove</button>
                                        </div>
                                    ) : (
                                        <div className={"flex gap-2"}>
                                            <input
                                                type="text"
                                                value={discountCodeInput}
                                                onChange={e => { setDiscountCodeInput(e.target.value); setDiscountCodeError(null); }}
                                                onKeyDown={e => e.key === 'Enter' && applyDiscountCode()}
                                                placeholder="Enter code"
                                                className={"border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1"}
                                            />
                                            <button
                                                type="button"
                                                onClick={applyDiscountCode}
                                                disabled={discountCodeLoading || !discountCodeInput.trim()}
                                                className={"border-0 bg-primary text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"}
                                            >
                                                {discountCodeLoading ? 'Checking...' : 'Apply'}
                                            </button>
                                        </div>
                                    )}
                                    {discountCodeError && <p className={"text-red-500 text-sm mt-2"}>{discountCodeError}</p>}
                                </div>
                            )}

                            {selectedSlots.length > 0 && isAuthenticated && service?.price > 0 && walletEnabled ? (
                                <div className={"mt-8"}>
                                    <h2 className={"text-lg mb-3"}>Payment Method</h2>
                                    <div className={"flex flex-col gap-2 text-sm"}>
                                        <label className={"flex items-center gap-2"}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="wallet"
                                                checked={paymentMethod === 'wallet'}
                                                onChange={() => setPaymentMethod('wallet')}
                                                disabled={!canUseWallet}
                                            />
                                            <span>Use wallet credit (£{(walletBalance / 100).toFixed(2)} available)</span>
                                        </label>
                                        <label className={"flex items-center gap-2"}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="processor"
                                                checked={paymentMethod === 'processor'}
                                                onChange={() => setPaymentMethod('processor')}
                                            />
                                            <span>Pay online</span>
                                        </label>
                                    </div>
                                    {!canUseWallet ? (
                                        <p className={"text-sm text-textGrey mt-2"}>You need {formatPrice(cartTotal)} in your wallet to book these appointments. <Link to="/dashboard/wallet">Top up your wallet</Link>.</p>
                                    ) : null}
                                </div>
                            ) : null}

                            {error && <p className={"text-red-500 text-sm mt-4"}>{error}</p>}
                            {waitlistMessage && <p className={"text-primary text-sm mt-4"}>{waitlistMessage}</p>}
                            {sumupError && <p className={"text-red-500 text-sm mt-4"}>{sumupError}</p>}

                            {sumupCheckoutId && (
                                <div className={"mt-4"}>
                                    <SumupWidget
                                        checkoutId={sumupCheckoutId}
                                        onSuccess={async () => {
                                            try {
                                                setSumupError(null);
                                                const result = await MainData.verifySumupBulkServiceBookings(sumupBookingId);
                                                if (result.status === 200) {
                                                    localStorage.removeItem('pendingServiceBulkCheckoutId');
                                                    navigate(`/dashboard/my-service-bookings?success=true`);
                                                }
                                            } catch {
                                                setSumupError('There was an issue verifying your payment. Please refresh.');
                                            }
                                        }}
                                        onError={message => setSumupError(message || 'Payment failed')}
                                    />
                                </div>
                            )}

                            <button
                                onClick={handleBook}
                                disabled={!selectedStaff || !selectedSlots.length || booking || !!sumupCheckoutId}
                                className={"inline-block bg-primary py-2 px-4 w-full text-white text-center rounded-md mt-6 border-0 disabled:opacity-50"}
                            >
                                {!isAuthenticated && selectedTime
                                    ? 'Login to Book'
                                    : booking ? 'Processing...'
                                    : sumupCheckoutId ? 'Payment Ready'
                                    : selectedSlots.length ? (paymentMethod === 'wallet' ? 'Book using wallet' : `Book ${selectedSlots.length} for ${formatPrice(discountedTotal)}${discountCodeData ? ` (was ${formatPrice(cartTotal)})` : ''}`)
                                    : 'Select a Date & Time'
                                }
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Service;
